import { forwardRef, useCallback, useMemo, useState } from 'react';
import { Image, Platform, StyleSheet, View, type ViewProps } from 'react-native';
import { colors } from '../../theme/colors';
import { tokens } from '../../theme/tokens';
import { Text } from './Text';

type AvatarSize = 'sm' | 'md' | 'lg' | 'xl';
type AvatarStatus = 'online' | 'offline' | 'busy' | 'away';

interface AvatarProps extends ViewProps {
  size?: AvatarSize;
  src?: string;
  name?: string;
  status?: AvatarStatus;
  color?: string;
  accentRing?: boolean;
}

const SIZE_MAP: Record<AvatarSize, number> = {
  sm: 28,
  md: 36,
  lg: 48,
  xl: 64,
};

const FONT_SIZE_MAP: Record<AvatarSize, number> = {
  sm: tokens.fontSize.xs,
  md: tokens.fontSize.sm,
  lg: tokens.fontSize.md,
  xl: tokens.fontSize.xl,
};

const STATUS_SIZE_MAP: Record<AvatarSize, number> = {
  sm: 8,
  md: 10,
  lg: 12,
  xl: 16,
};

const STATUS_BORDER_MAP: Record<AvatarSize, number> = {
  sm: 1.5,
  md: 2,
  lg: 2,
  xl: 2.5,
};

const STATUS_COLOR_MAP: Record<AvatarStatus, string> = {
  online: colors.status.success.main,
  offline: colors.neutral[500],
  busy: colors.status.error.main,
  away: colors.status.warning.main,
};

const getInitials = (name: string): string => {
  const trimmed = name.trim();
  if (!trimmed) return '';

  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts.at(-1)?.[0]}`.toUpperCase();
  }
  return trimmed.substring(0, 2).toUpperCase();
};

const RING_GAP = 3;
const RING_WIDTH = 2;

export const Avatar = forwardRef<View, AvatarProps>(
  ({ size = 'md', src, name, status, color, accentRing = false, style, ...rest }, ref) => {
    const [imageError, setImageError] = useState(false);
    const dimension = SIZE_MAP[size];
    const fontSize = FONT_SIZE_MAP[size];
    const initials = useMemo(() => (name ? getInitials(name) : ''), [name]);
    const backgroundColor = color || colors.primary.muted;

    const handleImageError = useCallback(() => {
      setImageError(true);
    }, []);

    const showImage = !!src && !imageError;

    const avatarContent = (
      <>
        {showImage ? (
          <Image
            source={{ uri: src }}
            style={[
              styles.image,
              {
                width: dimension,
                height: dimension,
                borderRadius: dimension / 2,
              },
            ]}
            onError={handleImageError}
          />
        ) : (
          <Text
            style={[
              styles.initialsText,
              {
                fontSize,
                lineHeight: fontSize * 1.2,
              },
            ]}
            color={colors.primary[500]}
          >
            {initials}
          </Text>
        )}
        {status && (
          <View
            style={[
              styles.statusIndicator,
              {
                width: STATUS_SIZE_MAP[size],
                height: STATUS_SIZE_MAP[size],
                borderRadius: STATUS_SIZE_MAP[size] / 2,
                borderWidth: STATUS_BORDER_MAP[size],
                backgroundColor: STATUS_COLOR_MAP[status],
              },
            ]}
          />
        )}
      </>
    );

    const avatarBase = {
      width: dimension,
      height: dimension,
      borderRadius: dimension / 2,
      backgroundColor: showImage ? 'transparent' : backgroundColor,
    };

    if (accentRing) {
      const ringDim = dimension + 2 * (RING_GAP + RING_WIDTH);
      return (
        <View
          ref={ref}
          style={[
            styles.ringContainer,
            {
              width: ringDim,
              height: ringDim,
              borderRadius: ringDim / 2,
              borderWidth: RING_WIDTH,
              padding: RING_GAP,
              borderColor: colors.primary[500],
            },
            style,
          ]}
          accessibilityRole="image"
          accessibilityLabel={name ? `Avatar for ${name}` : 'Avatar'}
          {...rest}
        >
          <View style={[styles.container, avatarBase]}>{avatarContent}</View>
        </View>
      );
    }

    const webHoverProps = Platform.select({
      web: {
        style: [styles.container, avatarBase, style] as any,
      },
      default: {},
    });

    return (
      <View
        ref={ref}
        style={[styles.container, avatarBase, style]}
        accessibilityRole="image"
        accessibilityLabel={name ? `Avatar for ${name}` : 'Avatar'}
        {...webHoverProps}
        {...rest}
      >
        {avatarContent}
      </View>
    );
  },
);

Avatar.displayName = 'Avatar';

const styles = StyleSheet.create({
  container: {
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    position: 'relative',
  },
  ringContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  image: {
    resizeMode: 'cover',
  },
  initialsText: {
    fontWeight: '600',
    textAlign: 'center',
  },
  statusIndicator: {
    position: 'absolute',
    bottom: 0,
    right: 0,
    borderColor: colors.background.primary,
  },
});
